import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Col, Container, Div, ExpanderColumn, Pagination, Row, Text } from '@pinpoint/design-kit';

import { appActions, onboardingActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { StatusFilterButtonGroup, GridWrapper } from 'src/components';
import { ButtonNav, CellActions, CellDate, CellStatus, CellTag, CellText } from 'src/components/onboarding';
import { ApproveOnboarding, ConfirmOnboarding } from 'src/components/dialogs';
import CONSTANTS from 'src/store/constants';
import { formatNumber, getOnboardingStatusDescription, noOp } from 'src/functions';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const uuidPathPrefixRegex = /^client_upload_det\/[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}\//;

function OnboardingList({
  location: { search, pathname },
  history,
  app: { tenant },
  onboarding: {
    onboardings,
    fetchOnboardingsStatus,
    approveDetWorkflowStatus,
    confirmDetWorkflowStatus,
    onboardingsPage,
    onboardingsPages,
    onboardingsPerPage,
    // onboardingsTotal,
    gridState,
    controlledGridState,
    maxGridRowSelection,
    selectedGridRows,
  },
  setGridState,
  fetchOnboardings,
  enqueueNotification,
  selectGridRow,
  deselectGridRow,
  getFileViaSignedUrl,
  approveOnboarding,
  confirmOnboarding,
  cancelOnbFetch,
  unset,
}) {
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [cufIdToApprove, setCufIdToApprove] = useState(null);
  const [cufIdToConfirm, setCufIdToConfirm] = useState(null);
  const [pageOptions, setPageOptions] = useState([1]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedCode, setSelectedCode] = useState(new URLSearchParams().get('code'));
  const [sortInfo, setSortInfo] = useState([{ field: 'ts', direction: 'desc' }]);

  const selectedOnboardingId = useMemo(
    () => selectedGridRows?.map?.((row) => row?.original?.onboardingId)?.[0],
    [selectedGridRows]
  );

  const data = useMemo(
    () =>
      onboardings
        .filter((onboarding) => !!onboarding?.files?.length)
        .map((onboarding) => ({
          ...onboarding.files[0],
          onboardingId: onboarding.id,
          datasetName: onboarding.dataset_name,
          datasetTag: onboarding.dataset_tag,
          datasetId: onboarding.dataset_id,
          onboardingType: onboarding.type || 'ENRICHMENT',
          onboardingPayload: onboarding.payload,
          latestState: onboarding.files[0]?.states?.reduce?.((acc, cur) => (acc?.id > cur?.id ? acc : cur), { id: 0 }),
          subRows: onboarding.files.slice(1).map((sub) => ({
            ...sub,
            latestState: sub?.states?.reduce?.((acc, cur) => (acc?.id > cur?.id ? acc : cur), { id: 0 }),
          })),
        }))
        .filter(({ latestState }) => {
          switch (selectedCode) {
            case CONSTANTS.TAL_MODEL.FILTER.IN_PROGRESS:
              return [
                CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_RAW_CLIENT_UPLOAD,
                CONSTANTS.ONBOARDING_STATUS.API.ACCEPTED_RAW_CLIENT_UPLOAD,
                CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_CLIENT_UPLOAD,
                CONSTANTS.ONBOARDING_STATUS.API.ACCEPT_CLIENT_UPLOAD,
                CONSTANTS.ONBOARDING_STATUS.API.PROCESSED_BY_DET,
                CONSTANTS.ONBOARDING_STATUS.API.APPROVED_BY_ADMIN,
                CONSTANTS.ONBOARDING_STATUS.API.EXTRACTED_FOR_PUSH,
                CONSTANTS.ONBOARDING_STATUS.API.PACKAGED_FOR_PROVIDER,
                CONSTANTS.ONBOARDING_STATUS.API.PUSHED_TO_PROVIDER,
                CONSTANTS.ONBOARDING_STATUS.API.READY_FOR_PROVIDER,
                CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_FROM_PROVIDER,
              ].includes(latestState.state);
            case CONSTANTS.TAL_MODEL.FILTER.PENDING_APPROVAL:
              return [
                CONSTANTS.ONBOARDING_STATUS.API.DET_WAITING_FOR_CONFIRMATION,
                CONSTANTS.ONBOARDING_STATUS.API.WAITING_FOR_APPROVAL,
              ].includes(latestState.state);
            case CONSTANTS.TAL_MODEL.FILTER.ERRORED:
              return [
                CONSTANTS.ONBOARDING_STATUS.API.RECEIVED_RAW_CLIENT_UPLOAD_FAILURE,
                CONSTANTS.ONBOARDING_STATUS.API.ACCEPTED_RAW_CLIENT_UPLOAD_FAILURE,
                CONSTANTS.ONBOARDING_STATUS.API.PROCESSED_BY_DET_FAILURE,
              ].includes(latestState.state);
            case CONSTANTS.TAL_MODEL.FILTER.COMPLETED:
              return latestState.state === 'PROFILE_MATCHED';
            default:
              return true;
          }
        }),
    [onboardings, selectedCode]
  );
  const hasData = !!data?.length;

  const handleCloseApprove = useCallback(() => {
    setApproveDialogOpen(false);
    setCufIdToApprove(null);
  }, [setApproveDialogOpen, setCufIdToApprove]);

  const handleCloseConfirm = useCallback(() => {
    setConfirmDialogOpen(false);
    setCufIdToConfirm(null);
  }, [setConfirmDialogOpen, setCufIdToConfirm]);

  const handleApproveClick = useCallback((cufId) => {
    setCufIdToApprove(cufId);
    setApproveDialogOpen(true);
  }, []);

  const handleConfirmClick = useCallback((cufId) => {
    setCufIdToConfirm(cufId);
    setConfirmDialogOpen(true);
  }, []);

  const checkSelectable = useCallback((row) => {
    const states = row?.original?.states;
    const latestState = row?.original?.latestState;
    const hasWaitingForAdmin =
      states.findIndex((state) => state?.state === CONSTANTS.ONBOARDING_STATUS.API.WAITING_FOR_APPROVAL) >= 0;
    const isActuarial = row?.original?.datasetTag === CONSTANTS.SEGMENT.TAG.ACTUARIAL;
    const latestStateIsError = !latestState?.state || /(_FAIL|_ERR)/.test(latestState?.state);
    return hasWaitingForAdmin && isActuarial && !latestStateIsError;
  }, []);

  const handleConfirmOnboarding = useCallback(() => {
    confirmOnboarding(cufIdToConfirm);
  }, [cufIdToConfirm, confirmOnboarding]);

  const handleApproveOnboarding = useCallback(() => {
    approveOnboarding(cufIdToApprove);
  }, [cufIdToApprove, approveOnboarding]);

  const handleFilterButtonClick = useCallback(
    (filterType) => {
      const params = new URLSearchParams();
      if (selectedCode === filterType) params.delete('code');
      else params.set('code', filterType);
      history.replace(`${pathname}?${params.toString()}`);
    },
    [history, pathname, selectedCode]
  );

  const columns = useMemo(
    () => [
      ExpanderColumn,
      {
        Header: (
          <Text textSize="small" data-debug="filename-client-hdr">
            File Name
          </Text>
        ),
        id: 'fileName',
        accessor: 'filename_client',
        disableSortBy: true,
        justify: 'left',
        padding: { l: '.3125rem' },
        cellPadding: { l: '.3125rem' },
        style: { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' },
        Cell: CellText,
      },
      {
        Header: (
          <Text textSize="small" data-debug="dataset-name-hdr">
            Dataset Name
          </Text>
        ),
        id: 'datasetName',
        disableSortBy: true,
        accessor: 'states',
        justify: 'left',
        style: { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' },
        Cell: ({ value }) => {
          const receivedRawClientUpload = value.find(({ state }) => state === 'RECEIVED_RAW_CLIENT_UPLOAD');
          const displayText = get(receivedRawClientUpload || {}, 'payload.user_payload.datasetName', '-');
          return CellText({ value: displayText });
        },
      },
      {
        Header: (
          <Text textSize="small" data-debug="tag-hdr">
            Tag
          </Text>
        ),
        id: 'tag',
        justify: 'left',
        disableSortBy: true,
        accessor: 'datasetTag',
        Cell: CellTag,
      },
      {
        Header: (
          <Text textSize="small" data-debug="records-hdr">
            Initial Records
          </Text>
        ),
        id: 'records',
        disableSortBy: true,
        justify: 'left',
        accessor: 'initial_num_rows',
        Cell: ({ value }) => CellText({ value: value ? formatNumber(value) : '-' }),
      },
      {
        Header: (
          <Text textSize="small" data-debug="phase-hdr">
            Phase
          </Text>
        ),
        id: 'phase',
        justify: 'left',
        accessor: 'latestState',
        Cell: ({ value: { state = '-' } }) => CellText({ value: getOnboardingStatusDescription(state) }),
      },
      {
        Header: (
          <Text textSize="small" data-debug="actions-hdr">
            Actions
          </Text>
        ),
        id: 'actions',
        justify: 'left',
        disableSortBy: true,
        accessor: ({
          id: cufId,
          workflows,
          datasetName,
          datasetTag,
          onboardingType,
          onboardingPayload,
          onboardingId,
          latestState,
        }) => {
          const isReOnboardable = datasetName && datasetTag && onboardingType && onboardingPayload;
          return {
            cufId,
            workflows,
            latestState,
            datasetName,
            datasetTag,
            onboardingType,
            onboardingPayload,
            onboardingId,
            isReOnboardable,
          };
        },
        Cell: ({ value }) =>
          CellActions({
            value,
            history,
            handleApproveClick,
            handleConfirmClick,
            // handleDownloadClick,
          }),
      },
      {
        Header: (
          <Text textSize="small" data-debug="onboarded-hdr">
            Onboarded
          </Text>
        ),
        id: 'onboarded',
        justify: 'left',
        accessor: (entity) => dayjs.utc(get(entity, 'ts')).toDate(),
        sortType: 'datetime',
        sortDescFirst: true,
        // eslint-disable-next-line react/prop-types
        Cell: CellDate,
        padding: { r: '0.6875rem' },
        cellPadding: { r: '0.6875rem' },
      },
      {
        Header: (
          <Text textSize="small" data-debug="status-hdr">
            Status
          </Text>
        ),
        id: 'status',
        cellJustify: 'center',
        justify: 'center',
        accessor: 'latestState',
        disableSortBy: true,
        Cell: ({ value: { state = '-' } }) => CellStatus({ value: state }),
      },
    ],
    [history, handleApproveClick, handleConfirmClick]
  );

  const getData = useCallback(
    ({ nextPage = false, prevPage = false, perPage, targetPage }) => {
      const query = {
        order_by: sortInfo,
        fields:
          'items{id,dataset_name,dataset_tag,dataset_id,payload,files{id,filename_client,workflows{id,dag_execution_date},states{id,state,payload{user_payload}},initial_num_rows,ts}},*',
      };
      if (targetPage) query.page = targetPage;
      if (nextPage) query.page = onboardingsPage + 1;
      if (prevPage) query.page = onboardingsPage === 1 ? 1 : onboardingsPage - 1;
      query.perPage = Number(perPage || onboardingsPerPage);
      unset('onboardings');
      unset('fetchOnboardingsStatus');
      fetchOnboardings(query);
    },
    [fetchOnboardings, onboardingsPage, onboardingsPerPage, sortInfo, unset]
  );

  // NOTE: if there are query params, process them looking for an upload file id
  // and a command (approve, confirm, and download)
  useEffect(() => {
    const params = new URLSearchParams(search);
    const command = params.get('do');
    const cufId = params.get('upload_file_id');
    const key = params.get('key');
    const bucket = params.get('bucket');
    params.delete('do');
    params.delete('upload_file_id');
    params.delete('key');
    params.delete('bucket');
    switch (command) {
      case 'approve':
        setCufIdToApprove(cufId);
        setApproveDialogOpen(true);
        history.replace(`${pathname}?${params.toString()}`);
        break;
      case 'download':
        if (bucket && key) getFileViaSignedUrl(bucket, key.replace(uuidPathPrefixRegex, ''));
        history.replace(pathname);
        break;
      case 'confirm':
        setCufIdToConfirm(cufId);
        setConfirmDialogOpen(true);
        history.replace(pathname);
        break;
      default:
        // NOTE: if no command, process other params
        setSelectedCode(params.get('code'));
        break;
    }
  }, [getFileViaSignedUrl, history, pathname, search]);

  useEffect(() => {
    if (confirmDetWorkflowStatus === CONSTANTS.API.STARTED) {
      const message = 'Your onboarding will resume shortly...';
      enqueueNotification(message, { autoClose: true });
    } else if (confirmDetWorkflowStatus === CONSTANTS.API.FAILED) {
      const message = 'There was an error when confirming your onboarding. Please contact Pinpoint.';
      enqueueNotification(message, { autoClose: false });
    }
  }, [confirmDetWorkflowStatus, enqueueNotification]);

  useEffect(() => {
    if (approveDetWorkflowStatus === CONSTANTS.API.STARTED) {
      const message = 'The onboarding will resume shortly...';
      enqueueNotification(message, { autoClose: true });
    } else if (approveDetWorkflowStatus === CONSTANTS.API.FAILED) {
      const message = 'There was an error when approving the onboarding.';
      enqueueNotification(message, { autoClose: false });
    }
  }, [approveDetWorkflowStatus, enqueueNotification]);

  useEffect(() => {
    const nextOptions = Array.from({ length: onboardingsPages }, (_, i) => i + 1);
    setPageOptions(nextOptions);
    setPageIndex(nextOptions.findIndex((elem) => elem === onboardingsPage));
  }, [onboardingsPages, onboardingsPage, onboardingsPerPage]);

  useEffect(() => {
    if (gridState?.sortBy?.length) {
      let field = 'ts';
      if (gridState.sortBy[0].id === 'records') field = 'initial_num_rows';
      const direction = gridState.sortBy[0].desc ? 'desc' : 'asc';
      setSortInfo([{ field, direction }]);
    }
  }, [gridState.sortBy]);

  useEffect(() => {
    if (tenant) getData({ perPage: onboardingsPerPage, targetPage: onboardingsPage });
  }, [tenant, getData, onboardingsPage, onboardingsPerPage]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => cancelOnbFetch, []);

  return (
    <Container
      d="flex"
      flexDir="column"
      w="100%"
      minW="100%"
      maxW="100%"
      h="100%"
      minH="100%"
      maxH="100%"
      data-debug="onb-list-cntr"
    >
      <ButtonNav goTo={history.push} onbId={selectedOnboardingId} mode="list" />
      <Row flexGrow="1" data-debug="grid-row" m={{ t: '0.5rem', x: '0.5rem' }}>
        <Col size="12" textSize="small" bg="white" rounded="md" gutterWidth="0" d="flex" flexDir="column">
          <Row m={{ l: '2rem' }} p={{ t: '1rem', r: '1rem' }}>
            <Col size="6" gutterWidth="0" d="flex" align="center">
              <Text textSize="subtitle" textWeight="500" textColor="navyBlue" verticalAlign="center">
                Onboarding Log
              </Text>
            </Col>
            <Col size="6" justify="flex-end" d="flex" align="baseline">
              <StatusFilterButtonGroup selectedCode={selectedCode} onFilterButtonClick={handleFilterButtonClick} />
            </Col>
          </Row>
          {hasData && (
            <GridWrapper
              DivProps={{ m: {}, flexGrow: '1' }}
              hasData={hasData}
              apiStatus={fetchOnboardingsStatus}
              hasSelection
              enableSelection={checkSelectable}
              maxSelection={maxGridRowSelection}
              rawData={onboardings}
              data={data}
              columns={columns}
              controlledGridState={controlledGridState}
              gridState={gridState}
              getData={() => getData()}
              setGridState={setGridState}
              onRowSelect={selectGridRow}
              onRowDeselect={deselectGridRow}
              widths="2rem 3rem 3fr 2fr 1fr 1fr 1fr 0.75fr 0.75fr 4rem"
            />
          )}
          <Div>
            <Pagination
              canPreviousPage={onboardingsPage !== 1}
              canNextPage={onboardingsPage !== onboardingsPages}
              pageOptions={pageOptions}
              pageCount={onboardingsPages}
              gotoPage={(targetPage) => getData({ targetPage })}
              nextPage={() => getData({ nextPage: true })}
              previousPage={() => getData({ prevPage: true })}
              setPageSize={(perPage) => getData({ targetPage: 1, perPage })}
              pageIndex={pageIndex}
              pageSize={onboardingsPerPage}
            />
          </Div>
        </Col>
      </Row>
      <ConfirmOnboarding
        isOpen={confirmDialogOpen}
        actions={{
          cancel: { on: noOp },
          close: { on: handleCloseConfirm },
          handlers: [{ on: handleConfirmOnboarding }],
        }}
      />
      <ApproveOnboarding
        isOpen={approveDialogOpen}
        actions={{
          cancel: { on: noOp },
          close: { on: handleCloseApprove },
          handlers: [{ on: handleApproveOnboarding }],
        }}
      />
    </Container>
  );
}

const mapStateToProps = ({ app, onboarding }, ownProps) => ({
  app,
  onboarding,
  ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  fetchOnboardings: (query) => dispatch(onboardingActions.fetchOnboardings(query)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  setGridState: (state) => dispatch(onboardingActions.setGridState(state)),
  deselectGridRow: (segmentId) => dispatch(onboardingActions.deselectGridRow(segmentId)),
  selectGridRow: (segmentId) => dispatch(onboardingActions.selectGridRow(segmentId)),
  getFileViaSignedUrl: (bucket, key) => dispatch(appActions.getFileViaSignedUrl(bucket, key)),
  approveOnboarding: (uploadFileId) => dispatch(onboardingActions.approveDetWorkflow(uploadFileId)),
  confirmOnboarding: (uploadFileId) => dispatch(onboardingActions.confirmDetWorkflow(uploadFileId)),
  cancelOnbFetch: () => dispatch(onboardingActions.cancelFetchOnboardings()),
  unset: (key) => dispatch(onboardingActions.unset(key)),
});

OnboardingList.propTypes = {
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
  }).isRequired,
  onboarding: PropTypes.shape({
    selectedGridRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    maxGridRowSelection: PropTypes.number.isRequired,
    gridState: PropTypes.shape({
      sortBy: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          desc: PropTypes.bool,
        })
      ),
    }).isRequired,
    controlledGridState: PropTypes.shape({}).isRequired,
    onboardingsPage: PropTypes.number.isRequired,
    onboardingsPages: PropTypes.number.isRequired,
    onboardingsPerPage: PropTypes.number.isRequired,
    onboardingsTotal: PropTypes.number.isRequired,
    onboardings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fetchOnboardingsStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    approveDetWorkflowStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    confirmDetWorkflowStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    downloadDetWorkflowStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
  }).isRequired,
  fetchOnboardings: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  setGridState: PropTypes.func.isRequired,
  selectGridRow: PropTypes.func.isRequired,
  deselectGridRow: PropTypes.func.isRequired,
  getFileViaSignedUrl: PropTypes.func.isRequired,
  approveOnboarding: PropTypes.func.isRequired,
  confirmOnboarding: PropTypes.func.isRequired,
  cancelOnbFetch: PropTypes.func.isRequired,
  unset: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(OnboardingList));
